import React, { useEffect, useRef } from 'react';
import usePageMetadata from '../utils/usePageMetadata';
import './CookiePolicy.css';

const CookiePolicy = () => {
  const reportContainerRef = useRef(null);

  usePageMetadata({
    title: 'Cookie Policy | The Ad Shop Consent & Tracking Practices',
    description:
      'Review The Ad Shop’s cookie usage, consent management practices, and real-time cookie declaration powered by Cookie Script CMP.',
    canonicalUrl: 'https://theadshop.co/cookies',
    meta: [
      { property: 'og:title', content: 'Cookie Policy | The Ad Shop Consent & Tracking Practices' },
      {
        property: 'og:description',
        content:
          'Understand how The Ad Shop uses essential, performance, and advertising cookies, with a live cookie declaration report.',
      },
      { property: 'og:type', content: 'article' },
      { property: 'og:url', content: 'https://theadshop.co/cookies' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Cookie Policy | The Ad Shop Consent & Tracking Practices' },
      {
        name: 'twitter:description',
        content:
          'See detailed information about the cookies The Ad Shop sets and how to manage your consent preferences.',
      },
    ],
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'The Ad Shop Cookie Policy',
      url: 'https://theadshop.co/cookies',
      description:
        'Cookie policy describing The Ad Shop’s use of cookies and consent management platform for compliance.',
      isPartOf: {
        '@type': 'WebSite',
        name: 'The Ad Shop',
        url: 'https://theadshop.co/',
      },
    },
  });

  useEffect(() => {
    if (!reportContainerRef.current) {
      return undefined;
    }

    // Clear container to avoid duplicate embeds on client-side navigation
    reportContainerRef.current.innerHTML = '';

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.charset = 'UTF-8';
    script.src = 'https://report.cookie-script.com/r/8fc04193dc546cb473086dda9802b4cd.js';
    script.setAttribute('data-cookiescriptreport', 'report');
    script.async = true;

    reportContainerRef.current.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  return (
    <div className="cookie-policy-page">
      <div className="cookie-policy-container">
        <header className="cookie-policy-header">
          <h1>Cookie Policy</h1>
          <p className="effective-date">Effective Date: November 9, 2025</p>
          <p className="intro">
            This Cookie Policy explains how The Ad Shop uses cookies and similar technologies on https://theadshop.co and our
            related services. It should be read together with our Privacy Policy.
          </p>
        </header>

        <section className="cookie-policy-section">
          <h2>What Are Cookies?</h2>
          <p>
            Cookies are small data files stored on your device when you visit a website. They help us remember your
            preferences, keep you signed in, analyze how you use the platform, and deliver a secure, consistent
            experience. We also use similar technologies such as pixels, tags, and local storage (collectively referred
            to as “cookies” in this policy).
          </p>
        </section>

        <section className="cookie-policy-section">
          <h2>How We Use Cookies</h2>
          <p>We categorize the cookies we use as follows:</p>
          <ul>
            <li>
              <strong>Strictly Necessary</strong> – Required for core functionality such as authentication and security.
            </li>
            <li>
              <strong>Performance &amp; Analytics</strong> – Help us understand usage patterns and improve the product.
            </li>
            <li>
              <strong>Functionality</strong> – Remember preferences and personalize your experience.
            </li>
            <li>
              <strong>Advertising &amp; Third Party</strong> – Support integrations with services like Google Analytics,
              AdSense, and compliance tools.
            </li>
          </ul>
          <p>
            Some cookies are placed by third parties acting on our behalf. These providers are bound by their own
            privacy and cookie policies, as well as contractual obligations with us.
          </p>
        </section>

        <section className="cookie-policy-section">
          <h2>Manage Cookie Preferences</h2>
          <p>
            You can manage or withdraw consent for non-essential cookies at any time using the Cookie Script banner on
            our site. Most browsers also allow you to block or delete cookies through their settings. Disabling certain
            cookies may impact your ability to use The Ad Shop.
          </p>
        </section>

        <section className="cookie-policy-section">
          <h2>Cookie Declaration</h2>
          <p>
            The following table is automatically generated by our consent management platform and lists the cookies
            currently in use on The Ad Shop, including the provider, purpose, and retention period.
          </p>
          <div className="cookie-report-container" ref={reportContainerRef} />
        </section>

        <section className="cookie-policy-section">
          <h2>Changes to This Policy</h2>
          <p>
            We may update this Cookie Policy to reflect changes in technology, regulations, or our practices. The “Effective
            Date” above will indicate the latest version. Material changes will be communicated via the platform or email.
          </p>
        </section>

        <section className="cookie-policy-section">
          <h2>Contact</h2>
          <p>
            If you have questions about this policy or our use of cookies, contact us at{' '}
            <a href="mailto:support@theadshop.co">support@theadshop.co</a>.
          </p>
        </section>
      </div>
    </div>
  );
};

export default CookiePolicy;


